import type { PeerView } from '../../server/protocol';

/**
 * Mathematik hinter dem Folgen-Modus: Ansichten vergleichen (Senden/Änderungs-
 * erkennung) und zur Zielansicht eines Teilnehmers glätten (Abrufen).
 */

/** Toleranzen für „Ansicht praktisch gleich“ (Meter bzw. Radiant). */
const TARGET_EPS = 1e-4;
const DIST_EPS = 1e-4;
const ANGLE_EPS = 1e-5;

/** Winkel auf [−π, π) normalisieren — der Azimut akkumuliert über volle Drehungen. */
export function normAngle(a: number): number {
  return a - Math.PI * 2 * Math.round(a / (Math.PI * 2));
}

/** true, wenn beide Ansichten praktisch identisch sind (null zählt immer als verschieden). */
export function peerViewEqual(a: PeerView | null, b: PeerView | null): boolean {
  if (!a || !b) return false;
  if (Math.abs(a.distance - b.distance) > DIST_EPS) return false;
  if (Math.abs(normAngle(a.azimuth - b.azimuth)) > ANGLE_EPS) return false;
  if (Math.abs(a.elevation - b.elevation) > ANGLE_EPS) return false;
  return (
    Math.abs(a.target[0] - b.target[0]) <= TARGET_EPS &&
    Math.abs(a.target[1] - b.target[1]) <= TARGET_EPS &&
    Math.abs(a.target[2] - b.target[2]) <= TARGET_EPS
  );
}

/**
 * Ein Glättungsschritt Richtung Zielansicht: `k`-Anteil der Differenz pro Aufruf,
 * Azimut über den kürzesten Winkel. Bei Konvergenz wird das Zielobjekt selbst
 * zurückgegeben — der Aufrufer erkennt daran, dass die Kamera nicht mehr bewegt
 * werden muss.
 */
export function lerpPeerViewStep(cur: PeerView, target: PeerView, k: number): PeerView {
  const dAz = normAngle(target.azimuth - cur.azimuth);
  const dTarget = Math.hypot(
    target.target[0] - cur.target[0],
    target.target[1] - cur.target[1],
    target.target[2] - cur.target[2]
  );
  if (
    Math.abs(dAz) <= ANGLE_EPS &&
    Math.abs(target.elevation - cur.elevation) <= ANGLE_EPS &&
    Math.abs(target.distance - cur.distance) <= DIST_EPS &&
    dTarget <= TARGET_EPS
  ) {
    return target;
  }
  const mix = (a: number, b: number): number => a + (b - a) * k;
  return {
    target: [mix(cur.target[0], target.target[0]), mix(cur.target[1], target.target[1]), mix(cur.target[2], target.target[2])],
    distance: mix(cur.distance, target.distance),
    azimuth: normAngle(cur.azimuth + dAz * k),
    elevation: mix(cur.elevation, target.elevation)
  };
}
